import { styled, Typography } from '@mui/material';
import type { CSSObject } from '@mui/material/styles';
import type { CellTextAlign } from 'utils/table/user/helios/heliosCell';

export const LineClamp2: CSSObject = {
  whiteSpace: 'normal',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};

const ALIGN_TO_JUSTIFY: Record<CellTextAlign, 'flex-start' | 'center' | 'flex-end'> = {
  Left: 'flex-start',
  Center: 'center',
  Right: 'flex-end',
};

const ALIGN_TO_TEXT: Record<CellTextAlign, 'left' | 'center' | 'right'> = {
  Left: 'left',
  Center: 'center',
  Right: 'right',
};

export const Wrapper = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '0 12px 20px',
});

export const CardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '38px',
  padding: '2px 4px 2px 8px',
  borderBottom: `1px solid ${theme.palette.secondary.dark}`,
}));

export const CardHeaderActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  marginLeft: 'auto',
});

export const CardContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isNew',
})<{ isNew: boolean }>(({ isNew }) => ({
  padding: '10px 12px',
  display: 'grid',
  gridTemplateColumns: isNew ? '1fr' : '1fr 1fr',
  gap: isNew ? '12px' : '8px 14px',
}));

export const EditButtons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
  padding: '0 12px 12px',
});

export const CardRow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  minWidth: 0,
});

export const CellLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== 'labelAlign',
})<{ labelAlign?: CellTextAlign }>(({ theme, labelAlign }) => ({
  color: theme.palette.text.secondary,
  fontFamily: 'inherit',
  fontSize: '10px',
  fontWeight: 500,
  letterSpacing: '0.2px',
  lineHeight: '1.3',
  opacity: 0.6,
  textAlign: labelAlign ? (ALIGN_TO_TEXT[labelAlign] ?? 'left') : 'left',
  ...LineClamp2,
}));

export const Card = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isEditing',
})<{ isEditing: boolean }>(({ isEditing, theme }) => ({
  borderRadius: '14px',
  overflow: 'hidden',
  boxShadow: isEditing ? '0 2px 10px rgba(43,54,116,0.07)' : '0 1px 2px rgba(43,54,116,0.05), 0 3px 14px rgba(43,54,116,0.08)',
  border: isEditing ? `1px solid ${theme.palette.secondary.dark}` : 'none',
  borderLeft: isEditing ? `3px solid ${theme.palette.primary.main}` : undefined,
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.paper,
  cursor: isEditing ? 'default' : 'pointer',
  userSelect: 'none',
  transition: 'box-shadow 0.15s ease, transform 0.1s ease',
  '&:active': {
    transform: isEditing ? 'scale(1)' : 'scale(0.985)',
    boxShadow: isEditing ? undefined : '0 1px 3px rgba(43,54,116,0.05)',
  },
}));

export const CellValueWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'uiDefinition' && prop !== 'valueAlign',
})<{ uiDefinition?: boolean; valueAlign?: CellTextAlign }>(({ uiDefinition, valueAlign }) => ({
  display: 'flex',
  justifyContent: valueAlign ? (ALIGN_TO_JUSTIFY[valueAlign] ?? 'flex-start') : 'flex-start',
  alignItems: 'center',
  borderRadius: uiDefinition ? '4px' : 0,
  padding: uiDefinition ? '1px 6px' : '0',
  overflow: 'hidden',
  minHeight: '18px',
  '& .MuiTypography-root': {
    ...LineClamp2,
  },
}));

export const EditLabelTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'labelAlign',
})<{ labelAlign?: CellTextAlign }>(({ labelAlign }) => ({
  fontSize: '10px',
  fontWeight: 500,
  opacity: 0.6,
  lineHeight: 1.3,
  marginBottom: '2px',
  textAlign: labelAlign ? (ALIGN_TO_TEXT[labelAlign] ?? 'left') : 'left',
  ...LineClamp2,
}));

export const TextContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'valueAlign',
})<{ valueAlign?: CellTextAlign }>(({ valueAlign, theme }) => ({
  maxWidth: '100%',
  minWidth: 0,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '1.4',
  color: theme.palette.text.primary,
  textAlign: valueAlign ? (ALIGN_TO_TEXT[valueAlign] ?? 'left') : 'left',
  '& .MuiTypography-root': {
    ...LineClamp2,
  },
}));
